'use client'

import { useState, useEffect, useRef } from 'react'
import slugify from 'slugify'
import AnchorLink from 'react-anchor-link-smooth-scroll'

/**
 * A set of jump links that track the currently active section
 *
 * Used on pages:
 * - principles
 * - certifications
 *
 * @prop {Array} links Links to display in the menu
 * @prop {Object} intersectionObserverOptions Options for Intersection Observer (root, rootMargin, threshold)
 */

interface JumpNavLink {
	title: string
	slug?: string // This gets generated based on the title
	slugOverride?: string
}

interface JumpNavProps {
	links: JumpNavLink[]
	intersectionObserverOptions?: IntersectionObserverInit
}

export default function JumpNav({
	links,
	intersectionObserverOptions,
}: JumpNavProps): JSX.Element {
	const navEl = useRef(null)
	const [activeIndex, setActiveIndex] = useState(0)

	// Add slug value to links using the source key specified
	links.map((link) => {
		link.slug = link.slugOverride || slugifyJumpLink(link.title)
	})

	useEffect(() => {
		function initIntersectionObserver() {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							links.some((obj, i) => {
								if (obj.slug === entry.target.id) {
									setActiveIndex(i)
									return true
								}
							})
						}
					})
				},
				{
					root: null,
					rootMargin: '0px 0px -95% 0px',
					threshold: 0,
					...intersectionObserverOptions,
				}
			)

			links.forEach((d) => {
				observer.observe(document.querySelector(`#${d.slug}`))
			})
		}

		if (window.IntersectionObserver) {
			initIntersectionObserver()
		} else {
			import('intersection-observer').then(() => {
				initIntersectionObserver()
			})
		}

		import('stickyfilljs').then((stickyFill) => {
			stickyFill.addOne(navEl.current)
			navEl.current.classList.add('sticky')
		})
	}, [])

	return (
		<nav className="g-jump-nav" ref={navEl}>
			<ul>
				{links.map((d, i) => (
					<li key={d.slug} className="g-type-buttons-and-standalone-links">
						<AnchorLink
							className={i === activeIndex ? 'active' : ''}
							href={`#${d.slug}`}
						>
							{d.title}
						</AnchorLink>
					</li>
				))}
			</ul>
		</nav>
	)
}

// Expose this helper function for consuming components. This ensures consistency between where the
// target id is created (in the parent component) and the jump link. Uses slugify's remove option to
// strip characters that would make a messy id value.
export function slugifyJumpLink(link) {
	return slugify(link, {
		lower: true,
		remove: /[*+~.,()'"!:@?]/g,
	})
}
